import React, {
  type FC,
  type MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {type Path, Transforms} from 'slate';
import {ReactEditor, useReadOnly, useSlateStatic} from 'slate-react';

import {captureAndLogError, captureError} from '../../../../integrations';
import {useSelector} from '../../../../state/hooks';
import {useSection} from '../../../../state/views/section/hooks';
import type * as SectionTypes from '../../../../state/views/section/types';
import {ErrorBoundary} from '../../../ErrorBoundary';
import {WBSlateReduxBridgeContext} from '../../WBSlateReduxBridgeContext';
import * as S from './PanelGridElement.styles';
import {PanelGridError} from './PanelGridError';
import {type PanelGrid as PanelGridType} from './types';

type PanelGridProps = {
  element: PanelGridType;
  partRef: SectionTypes.Ref;
  updateDenormalizedOnNormalizedUpdate: MutableRefObject<boolean>;
  lastMirroredMetadata: MutableRefObject<SectionTypes.Config | null>;
};

/**
 * Backstop for a section part that went missing. PanelGridElement keeps the ref in a
 * mutable ref, so a renormalize can leave this prop pointing at a deleted part.
 * In case of this race, instead of crashing the whole report, just show an
 * error for the panel grid.
 */
export const PanelGrid: FC<PanelGridProps> = props => {
  const isSectionInState = useSelector(
    state => state.views.parts.section[props.partRef.id] != null
  );

  const missingSectionId = isSectionInState ? null : props.partRef.id;

  // effect, not render: fires once per missing id, after commit
  useEffect(() => {
    if (missingSectionId != null) {
      captureAndLogError(
        new Error(`section:${missingSectionId} could not be found in state`),
        'PanelGrid missing section'
      );
    }
  }, [missingSectionId]);

  if (!isSectionInState) {
    return <PanelGridError />;
  }

  return <LoadedPanelGrid {...props} />;
};

const LoadedPanelGrid: FC<PanelGridProps> = ({
  element,
  partRef,
  updateDenormalizedOnNormalizedUpdate,
  lastMirroredMetadata,
}) => {
  const {projectName, entityName, viewRef, panelSettingsRef} = useContext(
    WBSlateReduxBridgeContext
  );
  if (viewRef == null) {
    throw new Error(`panel grid rendered with null view ref`);
  }
  if (panelSettingsRef == null) {
    throw new Error(`panel grid rendered with null panel settings ref`);
  }
  const whole = useSection(partRef);
  const readOnly = useReadOnly();
  const editor = useSlateStatic();

  const pathRef = useRef<Path>(ReactEditor.findPath(editor, element));
  // oxlint-disable-next-line react/refs
  pathRef.current = ReactEditor.findPath(editor, element);

  /** On a normal change, quietly make a saved change. */
  useEffect(() => {
    if (updateDenormalizedOnNormalizedUpdate.current) {
      // record what we write so PanelGridElement can tell our own mirror from an
      // outside edit, no matter how many of these writes coalesce into one of its runs
      // oxlint-disable-next-line react/immutability
      lastMirroredMetadata.current = whole;
      Transforms.setNodes(
        editor,
        {metadata: whole},
        {
          at: pathRef.current,
        }
      );
    }
    // oxlint-disable-next-line react/immutability
    updateDenormalizedOnNormalizedUpdate.current = true;
  }, [
    editor,
    lastMirroredMetadata,
    pathRef,
    updateDenormalizedOnNormalizedUpdate,
    whole,
  ]);

  // logics for ErrorBoundary wrapping panel grids
  const errorParams = useMemo(
    () => ({entityName, projectName}),
    [entityName, projectName]
  );

  const logError = useCallback(
    (error: any) => {
      const key = readOnly ? 'PanelGridView' : 'PanelGridEdit';
      window.analytics?.track(`${key} error`, {
        errorParams,
        errorMessage: error.message,
        errorName: error.name,
        errorStack: error.stack,
      });
      captureError(error, `${key} ErrorBoundary error`);
    },
    [errorParams, readOnly]
  );

  const renderPanelGridError = useCallback(() => <PanelGridError />, []);

  return (
    <ErrorBoundary renderError={renderPanelGridError} onError={logError}>
      <S.ReportPanelGridSection
        entityName={entityName}
        projectName={projectName}
        panelSettingsRef={panelSettingsRef}
        sectionRef={partRef}
        viewRef={viewRef}
        readOnly={readOnly}
      />
    </ErrorBoundary>
  );
};
